import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import './DisputeDetailScreen.css';

import backIcon from '../../assets/backspace-icon.png';

function DisputeDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [selectedWinner, setSelectedWinner] = useState('');
  const [decisionReason, setDecisionReason] = useState('');

  useEffect(() => {
    const fetchDispute = async () => {
      try {
        const res = await api.get(`/admin/disputes/${id}`);
        setDispute(res.data);
        console.log('📥 Dispute loaded:', res.data);
      } catch (err) {
        console.error('❌ Failed to fetch dispute:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDispute();
  }, [id]);

  function getCountdown(deadline) {
    if (dispute?.orderId?.disputePaused) return '⏸️ Paused';
    const now = new Date();
    const diff = deadline - now;
    if (diff <= 0) return 'Past due';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    return `${days}d ${hours}h ${minutes}m`;
  }

  const isDynamic = dispute?.orderId?.productId?.pricingType === 'dynamic';

  const toggleDisputePause = async () => {
    try {
      await api.patch(`/escrow/admin/escrow/${dispute.orderId._id}/pause`);
      const refreshed = await api.get(`/admin/disputes/${id}`);
      setDispute(refreshed.data);
      console.log(`🔁 Dispute pause toggled: ${refreshed.data.orderId.disputePaused}`);
    } catch (err) {
      console.error('❌ Failed to pause/resume dispute:', err);
      alert('Failed to update dispute timer.');
    }
  };

  const handleApproveClick = (winner) => {
    setSelectedWinner(winner);
    setDecisionReason('');
    setShowModal(true);
  };


const submitResolution = async () => {
  if (!decisionReason.trim()) {
    alert('Please provide a reason for your decision.');
    return;
  }

  try {
    // 🔁 This triggers the actual escrow payout logic (you already wired it!)
    const payoutRes = await api.patch(`/escrow/admin/escrow/${dispute.orderId._id}/resolve`, {
      decision: selectedWinner
    });

    // ✅ Then mark the dispute as resolved with admin’s reason (no payout logic here)
    const decisionRes = await api.patch(`/admin/disputes/${dispute._id}/resolve`, {
      winner: selectedWinner,
      reason: decisionReason,
      decidedBy: 'barterfi_admin'
    });

    // ♻️ Refresh UI
    const refreshed = await api.get(`/admin/disputes/${dispute._id}`);
    setDispute(refreshed.data);
    setShowModal(false);

    alert(`✅ ${selectedWinner} has been approved and dispute resolved.`);
    console.log('🧾 Admin resolved dispute:', payoutRes.data);
  } catch (err) {
    console.error('❌ Failed to resolve dispute:', err);
    alert('Failed to approve party. Please try again.');
  }
};




  if (loading) return <div className="disputedetail-loading">Loading...</div>;
  if (!dispute) return <div className="disputedetail-error">Dispute not found.</div>;

  return (
    <div className="disputedetail-container">
      <div className="disputedetail-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="disputedetail-back-icon"
          onClick={() => navigate('/admin/disputes')}
        />
        <h2>Dispute Details</h2>
      </div>

      <div className="disputedetail-content">

<p><strong>Order ID:</strong> {dispute.orderId?._id}</p>
<p><strong>Dispute ID:</strong> {dispute._id}</p>


<p><strong>Status:</strong> {dispute.status}</p>



        {dispute.orderId?.productId && (
          <>
            <p><strong>Product:</strong> {dispute.orderId.productId.name}</p>
            <p><strong>Description:</strong> {dispute.orderId.productId.description}</p>
            <p><strong>Estimated Delivery:</strong> {dispute.orderId.productId.expectedDeliveryDays} days</p>
          </>
        )}

        {dispute.orderId?.autoReleaseDate && (
          <p>
            <strong>Deadline:</strong> {getCountdown(new Date(dispute.orderId.autoReleaseDate))}
          </p>
        )}

        {isDynamic ? (
          <>
            <p><strong>Amount (USD):</strong> ${dispute.orderId?.price ?? 'N/A'}</p>
            <p><strong>Amount (Pi):</strong> {dispute.orderId?.amount ?? 'N/A'} Pi</p>
          </>
        ) : (
          <p><strong>Amount (Pi):</strong> {dispute.orderId?.amount ?? 'N/A'} Pi</p>
        )}

        <p><strong>Buyer:</strong> {dispute.buyer}</p>
        <p><strong>Seller:</strong> {dispute.seller}</p>
        <p><strong>Reason:</strong> {dispute.reason || 'No reason provided'}</p>
        <p><strong>Created:</strong> {new Date(dispute.createdAt).toLocaleString()}</p>

        {dispute.mediaUrls?.length > 0 && (
          <div className="disputedetail-media-grid">
            <p><strong>Submitted Media:</strong></p>
            <div className="disputedetail-media-wrapper">
              {dispute.mediaUrls.map((url, idx) => {
                console.log(`🔍 Media ${idx + 1}:`, url);
                const lowerUrl = url.toLowerCase();
                const isVideo = lowerUrl.endsWith('.mp4') || lowerUrl.includes('/videos/');
                const isImage = lowerUrl.endsWith('.jpg') || lowerUrl.endsWith('.jpeg') || lowerUrl.endsWith('.png') || lowerUrl.endsWith('.gif');

                if (!isVideo && !isImage) {
                  console.warn(`⚠️ Unknown media type:`, url);
                  return null;
                }

                return isVideo ? (
                  <video key={idx} controls className="disputedetail-media-item" preload="metadata">
                    <source src={url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    key={idx}
                    src={url}
                    alt={`Media ${idx + 1}`}
                    className="disputedetail-media-item"
                  />
                );
              })}
            </div>
          </div>
        )}


{dispute.status !== 'resolved' && (
  <div className="disputedetail-actions">
    <button className="admin-btn approve-btn" onClick={() => handleApproveClick('buyer')}>
      Approve Buyer
    </button>
    <button className="admin-btn approve-btn" onClick={() => handleApproveClick('seller')}>
      Approve Seller
    </button>
    <button className="admin-btn pause-btn" onClick={toggleDisputePause}>
      {dispute.orderId?.disputePaused ? 'Resume Dispute' : 'Pause Dispute'}
    </button>
  </div>
)}


      </div>





      {showModal && (
        <div className="dispute-modal-overlay">
          <div className="dispute-modal">
            <h3>Submit Reason for Approving {selectedWinner}</h3>
            <textarea
              rows="4"
              value={decisionReason}
              onChange={(e) => setDecisionReason(e.target.value)}
              placeholder="Explain your decision..."
              className="dispute-modal-textarea"
            />
            <div className="dispute-modal-actions">
              <button onClick={submitResolution} className="admin-btn approve-btn">Submit</button>
              <button onClick={() => setShowModal(false)} className="admin-btn cancel-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DisputeDetailScreen;
